import { useState } from "react";
import useReveal from "../hooks/useReveal";
import Navbar from "../components/Navbar/Navbar";
import Newsletter from "../components/Newsletter/Newsletter";
import Footer from "../components/Footer/Footer";
import "./ContactUs.css";

export default function ContactUs() {
  useReveal();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hoveredContinent, setHoveredContinent] = useState(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: `${form.firstName} ${form.lastName}`, 
          email: form.email, 
          message: form.message 
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setForm({ firstName: "", lastName: "", email: "", message: "" });
        }, 4000);
      } else {
        const d = await res.json();
        setError(d.message || "Failed. Try again.");
      }
    } catch {
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setForm({ firstName: "", lastName: "", email: "", message: "" });
      }, 4000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />

      {/* ══ HERO ══ */}
      <section className="cu-hero">
        <div className="cu-hero-inner reveal">
          <h1>Let's discuss your game or app idea and turn it into a powerful digital experience.</h1>
          <p>We collaborate with clients across the globe, delivering innovative game and app solutions worldwide.</p>
        </div>
      </section>

      {/* ══ MAP SECTION ══ */}
      <section className="cu-map-section">
        <div className="cu-map-container">
          <img src={require("../assets/world-map.png")} alt="World presence map" className="cu-world-map" />
        </div>

        {/* Info bar */}
        <div className="cu-info-bar">
          <div className="cu-info-social">
            <span>Follow us</span>
            <div className="cu-social-icons">
              <a href="#"><svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg></a>
              <a href="#"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg></a>
              <a href="#"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></a>
              <a href="#"><svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6z"/><rect x="2" y="9" width="4" height="12" fill="currentColor"/><circle cx="4" cy="4" r="2" fill="currentColor"/></svg></a>
            </div>
          </div>
          <div className="cu-divider"/>
          <div className="cu-info-item">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.1 1.18 2 2 0 012.1.01h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>
            <span>+91 92113 43912</span>
          </div>
          <div className="cu-divider"/>
          <div className="cu-info-item">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <span>Unit No. 1017, 10th Floor, SS Omnia, Sector 88, Gurugram</span>
          </div>
        </div>
      </section>

      {/* ══ SAY HELLO FORM ══ */}
      <section className="cu-form-section">
        <div className="cu-form-container">
          <div className="cu-form-header reveal">
            <h2>Say hello</h2>
            <p>Have a project in mind or a question for us?<br/>We'd love to hear from you.</p>
          </div>

          <form className="cu-form reveal" onSubmit={onSubmit}>
            <div className="cu-form-row-2">
              <div className="cu-field">
                <label>First Name</label>
                <input type="text" name="firstName" value={form.firstName} onChange={onChange} required disabled={submitting}/>
              </div>
              <div className="cu-field">
                <label>Last Name</label>
                <input type="text" name="lastName" value={form.lastName} onChange={onChange} required disabled={submitting}/>
              </div>
            </div>
            
            <div className="cu-field">
              <label>Email Address</label>
              <input type="email" name="email" value={form.email} onChange={onChange} required disabled={submitting}/>
            </div>
            
            <div className="cu-field">
              <label>Message</label>
              <textarea name="message" rows="6" value={form.message} onChange={onChange} required disabled={submitting}/>
            </div>
            
            {submitted && (
              <div className="cu-success-msg">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                <span>Message sent successfully! We'll get back to you soon.</span>
              </div>
            )}
            
            {error && <p className="cu-error-msg">{error}</p>}
            
            <button type="submit" className="cu-btn-submit" disabled={submitting || submitted}>
              {submitting ? "Sending..." : submitted ? "Sent ✓" : "Send Message"}
            </button>
          </form>
        </div>
      </section>

      {/* ══ WHY CHOOSE ══ */}
      <section className="section" style={{ background: "var(--bg-primary)" }}>
        <div className="container reveal">
          <h2 className="section-title">Why Choose iGamesTech</h2>
          <p className="section-desc" style={{ maxWidth: "100%" }}>
            With years of experience in game and app development, our expert team delivers scalable,
            secure, and visually engaging solutions trusted by clients worldwide.
          </p>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </>
  );
}
